import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { 
  Shield, Users, UserCheck, UserX, Search, ChevronDown, 
  Star, Calendar, Clock, Mail, Check, X, AlertTriangle
} from 'lucide-react';

export default function AdminPractitioners() {
  const { token } = useAuth();
  const { currentColors } = useTheme();
  const [practitioners, setPractitioners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  useEffect(() => {
    loadPractitioners();
  }, [statusFilter]);

  const loadPractitioners = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('role', 'practitioner');
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      
      const response = await axios.get(`${API}/admin/users?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPractitioners(response.data.users || []);
    } catch (error) {
      console.error('Failed to load practitioners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadPractitioners();
  };

  const handleStatusChange = async (userId, newStatus) => {
    try {
      await axios.put(
        `${API}/admin/users/${userId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadPractitioners();
    } catch (error) {
      alert('Failed to update practitioner status');
    }
  };

  const handlePromoteToPractitioner = async (userId) => {
    try {
      await axios.put(
        `${API}/admin/users/${userId}`,
        { role: 'practitioner' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadPractitioners();
    } catch (error) {
      alert('Failed to promote user to practitioner');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { bg: 'rgba(34, 197, 94, 0.2)', color: '#22c55e', label: 'Active' },
      pending: { bg: 'rgba(234, 179, 8, 0.2)', color: '#eab308', label: 'Pending' },
      blocked: { bg: 'rgba(239, 68, 68, 0.2)', color: '#ef4444', label: 'Blocked' }
    };
    return badges[status] || badges.pending;
  };

  return (
    <div className="space-y-6" data-testid="admin-practitioners-page">
      {/* Header */}
      <div className="glass-card p-6">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="w-8 h-8" style={{ color: currentColors.primary }} />
          <div>
            <h1 className="text-2xl font-cinzel" style={{ color: currentColors.primary }}>
              Practitioner Management
            </h1>
            <p style={{ color: currentColors.textMuted }}>
              Manage healers, instructors, and practitioners
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="glass-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm" style={{ color: currentColors.textMuted }}>Total Practitioners</p>
              <p className="text-2xl font-cinzel" style={{ color: currentColors.text }}>
                {practitioners.length}
              </p>
            </div>
            <Users className="w-8 h-8" style={{ color: currentColors.secondary }} />
          </div>
        </div>
        <div className="glass-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm" style={{ color: currentColors.textMuted }}>Active</p>
              <p className="text-2xl font-cinzel text-green-400">
                {practitioners.filter(p => p.status === 'active').length}
              </p>
            </div>
            <UserCheck className="w-8 h-8 text-green-400" />
          </div>
        </div>
        <div className="glass-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm" style={{ color: currentColors.textMuted }}>Pending Approval</p>
              <p className="text-2xl font-cinzel text-yellow-400">
                {practitioners.filter(p => p.status === 'pending').length}
              </p>
            </div>
            <Clock className="w-8 h-8 text-yellow-400" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="glass-card p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search 
                className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" 
                style={{ color: currentColors.textMuted }} 
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search practitioners..."
                className="cosmic-input w-full pl-10"
                data-testid="practitioner-search"
              />
            </div>
          </form>

          {/* Status Filter */}
          <div className="relative">
            <button
              onClick={() => setShowStatusDropdown(!showStatusDropdown)}
              className="cosmic-input flex items-center gap-2 min-w-[150px]"
              data-testid="status-filter-btn"
            >
              <span className="capitalize">{statusFilter === 'all' ? 'All Status' : statusFilter}</span>
              <ChevronDown className="w-4 h-4 ml-auto" />
            </button>
            {showStatusDropdown && (
              <div 
                className="absolute top-full mt-2 right-0 glass-card py-2 min-w-[150px] z-50"
                onMouseLeave={() => setShowStatusDropdown(false)}
              >
                {['all', 'active', 'pending', 'blocked'].map((status) => (
                  <button
                    key={status}
                    onClick={() => {
                      setStatusFilter(status);
                      setShowStatusDropdown(false);
                    }}
                    className="w-full text-left px-4 py-2 capitalize hover:opacity-80 transition-all"
                    style={{ 
                      color: statusFilter === status ? currentColors.primary : currentColors.text,
                      backgroundColor: statusFilter === status ? `${currentColors.primary}10` : 'transparent'
                    }}
                  >
                    {status === 'all' ? 'All Status' : status}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Practitioners List */}
      <div className="glass-card overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12">
            <div 
              className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: `${currentColors.primary}30`, borderTopColor: currentColors.primary }}
            />
          </div>
        ) : practitioners.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 mx-auto mb-4" style={{ color: currentColors.textMuted }} />
            <p style={{ color: currentColors.textMuted }}>No practitioners found</p>
            <p className="text-sm mt-2" style={{ color: currentColors.textMuted }}>
              Users with the "practitioner" role will appear here
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: `1px solid ${currentColors.border}` }}>
                  <th className="text-left py-4 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>
                    Practitioner
                  </th>
                  <th className="text-left py-4 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>
                    Contact
                  </th>
                  <th className="text-left py-4 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>
                    Status
                  </th>
                  <th className="text-left py-4 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>
                    Joined
                  </th>
                  <th className="text-left py-4 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {practitioners.map((practitioner) => {
                  const statusBadge = getStatusBadge(practitioner.status);
                  return (
                    <tr 
                      key={practitioner.id}
                      className="hover:opacity-80 transition-all"
                      style={{ borderBottom: `1px solid ${currentColors.border}` }}
                      data-testid={`practitioner-row-${practitioner.id}`}
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: `${currentColors.secondary}30` }}
                          >
                            {practitioner.avatar_url ? (
                              <img 
                                src={practitioner.avatar_url} 
                                alt={practitioner.name}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <span style={{ color: currentColors.secondary }} className="font-cinzel text-lg">
                                {practitioner.name?.charAt(0)?.toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div>
                            <p style={{ color: currentColors.text }} className="font-medium">
                              {practitioner.name}
                            </p>
                            {practitioner.bio && (
                              <p className="text-sm line-clamp-1" style={{ color: currentColors.textMuted }}>
                                {practitioner.bio}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4" style={{ color: currentColors.textMuted }} />
                          <span className="text-sm" style={{ color: currentColors.text }}>
                            {practitioner.email}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span 
                          className="px-3 py-1 text-xs rounded-full"
                          style={{ backgroundColor: statusBadge.bg, color: statusBadge.color }}
                        >
                          {statusBadge.label}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm" style={{ color: currentColors.textMuted }}>
                          {new Date(practitioner.created_at).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex gap-2">
                          {practitioner.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleStatusChange(practitioner.id, 'active')}
                                className="p-2 rounded-lg text-green-400 hover:bg-green-500/10 transition-all"
                                title="Approve"
                                data-testid={`approve-btn-${practitioner.id}`}
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleStatusChange(practitioner.id, 'blocked')}
                                className="p-2 rounded-lg text-red-400 hover:bg-red-500/10 transition-all"
                                title="Reject"
                                data-testid={`reject-btn-${practitioner.id}`}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          {practitioner.status === 'active' && (
                            <button
                              onClick={() => handleStatusChange(practitioner.id, 'blocked')}
                              className="p-2 rounded-lg text-yellow-400 hover:bg-yellow-500/10 transition-all"
                              title="Block"
                              data-testid={`block-btn-${practitioner.id}`}
                            >
                              <UserX className="w-4 h-4" />
                            </button>
                          )}
                          {practitioner.status === 'blocked' && (
                            <button
                              onClick={() => handleStatusChange(practitioner.id, 'active')}
                              className="p-2 rounded-lg text-green-400 hover:bg-green-500/10 transition-all"
                              title="Unblock"
                              data-testid={`unblock-btn-${practitioner.id}`}
                            >
                              <UserCheck className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info Card */}
      <div 
        className="glass-card p-4 flex items-start gap-3"
        style={{ borderColor: `${currentColors.accent}40` }}
      >
        <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: currentColors.accent }} />
        <div>
          <p className="text-sm" style={{ color: currentColors.text }}>
            <strong>Practitioner Role:</strong> Users with the practitioner role can create and manage their own healing sessions, access the practitioner dashboard, and be featured on the platform.
          </p>
          <p className="text-sm mt-1" style={{ color: currentColors.textMuted }}>
            To assign a user the practitioner role, visit the User Management page.
          </p>
        </div>
      </div>
    </div>
  );
}
